import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Check, LayoutTemplate } from 'lucide-react';
import type { ThemeTemplateToken } from '@sleed/bizcommerce-api';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { SystemGrid, type SystemGridColumn } from '~/components/system/grid/system-grid';
import { serverMessage } from '~/lib/api/server-error';
import { useThemeTemplatesList, useThemeTemplateMutations } from '~/hooks/use-theme-templates';
import { tokenToForm, type ThemeTemplateFormValues } from '~/lib/schemas/theme-template';
import { ThemeTemplateEditDialog } from './theme-template-edit-dialog';

const Bool = ({ on }: { on: boolean }) =>
	on ? <Check size={16} className="text-success-fg" /> : <span className="text-foreground-disabled">—</span>;

/** Theme templates (Πρότυπα Θεμάτων) CRUD surface — parity with the legacy admin grid (Name/Code/For Products/For Categories). */
export function ThemeTemplatesGridView() {
	const { t } = useTranslation();
	const { data, isLoading, isError, refetch } = useThemeTemplatesList();
	const { remove } = useThemeTemplateMutations();

	const [editOpen, setEditOpen] = useState(false);
	const [editing, setEditing] = useState<ThemeTemplateFormValues | null>(null);
	const [deleteRow, setDeleteRow] = useState<ThemeTemplateToken | null>(null);

	const columns = useMemo<SystemGridColumn<ThemeTemplateToken>[]>(
		() => [
			{
				key: 'name',
				header: t('catalog.themeTemplates.columns.name'),
				width: 'minmax(0,1fr)',
				sortValue: o => o.nm ?? '',
				exportValue: o => o.nm ?? '',
				render: o => (
					<span className="truncate text-sm font-medium text-foreground" title={o.nm ?? ''}>
						{o.nm || '—'}
					</span>
				)
			},
			{
				key: 'code',
				header: t('catalog.themeTemplates.columns.code'),
				width: '220px',
				sortValue: o => o.cd ?? '',
				exportValue: o => o.cd ?? '',
				render: o => <span className="truncate font-mono text-xs text-foreground-muted">{o.cd || '—'}</span>
			},
			{
				key: 'isForProducts',
				header: t('catalog.themeTemplates.columns.isForProducts'),
				width: '140px',
				align: 'center',
				sortValue: o => (o.isfp ? 1 : 0),
				exportValue: o => (o.isfp ? t('common.yes') : t('common.no')),
				render: o => <Bool on={Boolean(o.isfp)} />
			},
			{
				key: 'isForCategories',
				header: t('catalog.themeTemplates.columns.isForCategories'),
				width: '150px',
				align: 'center',
				sortValue: o => (o.isfc ? 1 : 0),
				exportValue: o => (o.isfc ? t('common.yes') : t('common.no')),
				render: o => <Bool on={Boolean(o.isfc)} />
			}
		],
		[t]
	);

	const confirmDelete = () => {
		if (!deleteRow?.id) return;
		remove.mutate(deleteRow.id, {
			onSuccess: () => {
				toast.success(t('common.delete'));
				setDeleteRow(null);
			},
			onError: (err: unknown) => toast.error(serverMessage(err))
		});
	};

	return (
		<>
			<SystemGrid<ThemeTemplateToken>
				gridId="theme-templates"
				title={t('catalog.themeTemplates.title')}
				rows={data ?? []}
				loading={isLoading}
				error={isError}
				onRetry={() => void refetch()}
				getRowId={o => o.id ?? o.nm}
				columns={columns}
				searchPredicate={(o, q) => (o.nm ?? '').toLowerCase().includes(q) || (o.cd ?? '').toLowerCase().includes(q)}
				defaultSort={{ key: 'name', dir: 'asc' }}
				emptyIcon={<LayoutTemplate size={36} className="text-foreground-disabled" strokeWidth={1.25} />}
				addLabel={t('catalog.themeTemplates.add')}
				onAdd={() => {
					setEditing(null);
					setEditOpen(true);
				}}
				onEdit={o => {
					setEditing(tokenToForm(o));
					setEditOpen(true);
				}}
				onDelete={setDeleteRow}
				exportFilename="theme-templates"
			/>

			<ThemeTemplateEditDialog open={editOpen} onOpenChange={setEditOpen} initial={editing} />

			<Dialog open={Boolean(deleteRow)} onOpenChange={open => !open && setDeleteRow(null)}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t('catalog.themeTemplates.deleteTitle')}</DialogTitle>
						<DialogDescription>{t('catalog.themeTemplates.deleteBody', { name: deleteRow?.nm || '' })}</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button variant="ghost" onClick={() => setDeleteRow(null)}>
							{t('common.cancel')}
						</Button>
						<Button variant="destructive" onClick={confirmDelete} disabled={remove.isPending}>
							{t('common.delete')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}
